import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Alert, Typography, CircularProgress } from '@mui/material';
import userService from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
}

const defaultProfile: UserProfile = {
  firstName: '',
  lastName: '',
  email: ''
};

const ProfileTab: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const userData = await userService.getUserProfile();
        setProfile(userData || defaultProfile);
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load profile data');
        setProfile(defaultProfile);
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value || ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setIsLoading(true);
      await userService.updateProfile(profile);
      setSuccess('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(extractApiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h6" gutterBottom>
        Personal Information
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      
      <TextField
        margin="normal"
        required
        fullWidth
        id="firstName"
        label="First Name"
        name="firstName"
        value={profile.firstName || ''}
        onChange={handleChange}
        disabled={isLoading}
      />
      
      <TextField
        margin="normal"
        required
        fullWidth
        id="lastName"
        label="Last Name"
        name="lastName"
        value={profile.lastName || ''}
        onChange={handleChange}
        disabled={isLoading}
      />
      
      <TextField
        margin="normal"
        fullWidth
        id="email"
        label="Email Address"
        name="email"
        type="email"
        value={profile.email || ''}
        disabled
        helperText="Email address cannot be changed"
      />
      
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 3 }}
        disabled={isLoading}
      >
        {isLoading ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );
};

export default ProfileTab; 